import { lazy, Suspense } from 'react'
import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route } from 'react-router-dom'

import Page from './components/Page'
import RequireSubscription from './components/RequireSubscription'
import { SpinnerFullScreen } from './components/Spinner'
import { RootLayout } from './layouts/RootLayout'
import { DashboardLayout } from './layouts/DashboardLayout'
import { RequireAuth } from './components/RequireAuth'
import { LoginPage } from './pages/LoginPage'
import { SignupPage } from './pages/SignupPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { DashboardPage } from './pages/DashboardPage'
import { TeamPage } from './pages/TeamPage'
import { AddTeamPage } from './pages/AddTeamPage'
import { StripeSuccessPage } from './pages/StripeSuccessPage'
import { StripeCancelPage } from './pages/StripeCancelPage'
import { ProfilePage } from './pages/ProfilePage'
import { MailPdfProfilePage } from './pages/MailPdfProfilePage'
import { BillsPage } from './pages/BillsPage'
import { SubscriptionPage } from './pages/SubscriptionPage'
import { TeamMemberDetailPage } from './pages/TeamMemberDetailPage'
import { CGUAcceptPage } from './pages/CGUAcceptPage'
import { PublicCGUPage } from './pages/PublicCGUPage'
import { PublicPrivacyPage } from './pages/PublicPrivacyPage'
import { RequireCguAccept } from './components/RequireCguAccept'
import SectionsList from './containers/SectionsList'
import CalculsList from './containers/CalculsList'
import AllCalculsPage from './containers/AllCalculsPage'
import { calculDashboardRoutes } from './routes/calculRoutes'
import { isTeamFeatureEnabled } from './utils/featureFlags'

const SaveCalculPage = lazy(() => import('./containers/SaveCalculPage'))
const LoadPage = lazy(() => import('./containers/LoadPage'))
const SendResultEmailPage = lazy(() => import('./containers/SendResultEmailPage'))

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      {/* Public routes */}
      <Route index element={<LoginPage />} />
      <Route path="login" element={<LoginPage />} />
      <Route path="signup" element={<SignupPage />} />
      <Route path="forgot" element={<ForgotPasswordPage />} />
      <Route path="stripe/success" element={<StripeSuccessPage />} />
      <Route path="stripe/cancel" element={<StripeCancelPage />} />
      <Route path="cgu" element={<PublicCGUPage />} />
      <Route path="privacy" element={<PublicPrivacyPage />} />

      {/* Authenticated routes with shared dashboard layout */}
      <Route element={<RequireAuth><RequireCguAccept><DashboardLayout /></RequireCguAccept></RequireAuth>}>
        <Route path="dashboard" element={<Outlet />}>
          <Route index element={<DashboardPage />} />
          <Route
            path="sections"
            element={
              <RequireSubscription>
                <SectionsList />
              </RequireSubscription>
            }
          />
          <Route
            path="calculs-par-domaine"
            element={
              <RequireSubscription>
                <CalculsList />
              </RequireSubscription>
            }
          />
          <Route
            path="calculs"
            element={
              <RequireSubscription>
                <AllCalculsPage />
              </RequireSubscription>
            }
          />
          <Route path="tous-calculs" element={<Navigate to="/dashboard/calculs" replace />} />
          <Route path="CGUAccept" element={<CGUAcceptPage />} />
          <Route
            path="save"
            element={
              <Suspense fallback={<SpinnerFullScreen />}>
                <Page title="BatiKal | Sauvegarde">
                  <RequireSubscription>
                    <SaveCalculPage />
                  </RequireSubscription>
                </Page>
              </Suspense>
            }
          />
          <Route
            path="load/:mode"
            element={
              <Suspense fallback={<SpinnerFullScreen />}>
                <Page title="BatiKal | Sauvegardes">
                  <RequireSubscription>
                    <LoadPage />
                  </RequireSubscription>
                </Page>
              </Suspense>
            }
          />
          <Route
            path="SendEmail"
            element={
              <Suspense fallback={<SpinnerFullScreen />}>
                <Page title="BatiKal | Envoi e-mail">
                  <RequireSubscription>
                    <SendResultEmailPage />
                  </RequireSubscription>
                </Page>
              </Suspense>
            }
          />
          {calculDashboardRoutes}
        </Route>
        <Route path="subscription" element={<SubscriptionPage />} />
        {isTeamFeatureEnabled() ? (
          <>
            <Route path="team" element={<TeamPage />} />
            <Route path="team/member/:memberUid" element={<TeamMemberDetailPage />} />
            <Route path="team/add" element={<AddTeamPage />} />
          </>
        ) : (
          <Route path="team/*" element={<Navigate to="/subscription" replace />} />
        )}
        <Route path="bills" element={<BillsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="mail-profile" element={<MailPdfProfilePage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  )
)
